<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ToggleSwitch from "primevue/toggleswitch";
import { appliquerTheme, themeInitial } from "../theme/index";
import { memoriserLocale, type Locale } from "../locales/index";

const { t, locale } = useI18n();

const isDark = ref(themeInitial() === "sombre");
watch(isDark, (dark) => {
  appliquerTheme(dark ? "sombre" : "clair");
});

function changeLocale(next: Locale): void {
  locale.value = next;
  memoriserLocale(next);
}

const year = new Date().getFullYear();
</script>

<template>
  <div class="layout">
    <a
      class="skip-link"
      href="#content"
    >{{ t("layout.skipLink") }}</a>

    <header class="header">
      <img
        class="wordmark"
        :src="isDark ? '/fraxion-wordmark-white.png' : '/fraxion-wordmark-ink.png'"
        height="44"
        :alt="t('marque.nom')"
      >

      <div class="controls">
        <div
          class="language-group"
          role="group"
          :aria-label="t('bascules.langue')"
        >
          <button
            type="button"
            class="language-button"
            :class="{ active: locale === 'fr' }"
            :aria-pressed="locale === 'fr'"
            @click="changeLocale('fr')"
          >
            {{ t("langues.frCourt") }}
          </button>
          <button
            type="button"
            class="language-button"
            :class="{ active: locale === 'en' }"
            :aria-pressed="locale === 'en'"
            @click="changeLocale('en')"
          >
            {{ t("langues.enCourt") }}
          </button>
        </div>

        <label class="theme-toggle">
          <span class="theme-label">{{ t("bascules.themeSombre") }}</span>
          <span
            class="theme-label-short"
            aria-hidden="true"
          >{{ t("bascules.themeSombreCourt") }}</span>
          <ToggleSwitch
            v-model="isDark"
            :aria-label="t('bascules.themeSombre')"
          />
        </label>
      </div>
    </header>

    <main
      id="content"
      class="content"
      tabindex="-1"
    >
      <slot />
    </main>

    <footer class="footer">
      <span>{{ t("layout.copyright", { year }) }}</span>
      <span>{{ t("layout.tagline") }}</span>
    </footer>
  </div>
</template>

<style scoped>
.layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.skip-link {
  position: absolute;
  left: var(--space-4);
  top: -100px;
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-sm);
  background: var(--color-accent);
  color: var(--color-accent-ink);
  font-weight: var(--weight-semibold);
  z-index: 10;
}

.skip-link:focus {
  top: var(--space-2);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-6);
  padding: var(--space-3) var(--space-6);
  background: var(--header-bg);
  color: var(--header-text);
  border-bottom: 1px solid var(--header-border);
}

.wordmark {
  display: block;
  height: 44px;
  width: auto;
  margin: -4px 0;
}

.controls {
  display: flex;
  align-items: center;
  gap: var(--space-6);
}

.language-group {
  display: inline-flex;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.language-button {
  font-family: var(--font-body);
  font-size: var(--size-sm);
  font-weight: var(--weight-semibold);
  min-width: 44px;
  min-height: 44px;
  padding: var(--space-1) var(--space-3);
  border: 0;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.language-button + .language-button {
  border-left: 1px solid var(--color-border-strong);
}

.language-button:hover {
  color: var(--color-text);
}

.language-button.active {
  background: var(--color-accent);
  color: var(--color-accent-ink);
}

/* The label is the touch target: 44 px square even when its text is hidden on narrow screens */
.theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  min-width: 44px;
  min-height: 44px;
  cursor: pointer;
}

.theme-label,
.theme-label-short {
  font-size: var(--size-sm);
  color: var(--color-text-muted);
}

.theme-label-short {
  display: none;
}

.content {
  flex: 1;
  outline: none;
}

.footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--space-2) var(--space-6);
  padding: var(--space-6);
  background: var(--footer-bg);
  color: var(--footer-text);
  font-size: var(--size-sm);
}

@media (max-width: 560px) {
  .header {
    padding: var(--space-3) var(--space-4);
    gap: var(--space-3);
  }

  .controls {
    gap: var(--space-3);
  }

  .theme-label {
    display: none;
  }

  .theme-label-short {
    display: inline;
  }

  .footer {
    padding: var(--space-6) var(--space-4);
  }
}
</style>
